import React from 'react';
import { Navigate } from 'react-router-dom';
import { 
  isSessionValid, 
  hasAnyModule, 
  hasAllModules,
  isUserInGroup,
  hasFullAccess,
  canViewModule
} from '../../utils/auth';

/**
 * COMPONENTE DE PROTECCIÓN DE RUTAS
 * 
 * Protege rutas verificando:
 * 1. Si existe una sesión válida
 * 2. Si el usuario tiene los módulos/permisos necesarios
 * 3. Si el usuario pertenece a un grupo específico (Concesionario, Honda, Xerox)
 * 
 * Si no hay sesión, redirige al login.
 * Si no tiene permisos, redirige a una página de "Sin permisos".
 */
const ProtectedRoute = ({ 
  module, 
  anyOf, 
  allOf,
  groupId,
  anyGroup,
  requireFullAccess = false,
  children, 
  redirectTo = '/sin-permisos'
}) => {
  // 1. Verificar si hay sesión válida
  if (!isSessionValid()) {
    console.warn('ProtectedRoute: No hay sesión válida, redirigiendo al login');
    return <Navigate to="/login" replace />;
  }
  
  // 2. Si no se especificó ningún criterio, solo validar sesión
  if (!module && !anyOf && !allOf && !groupId && !anyGroup && !requireFullAccess) {
    // Ruta protegida solo por autenticación, sin permisos específicos
    return <>{children}</>;
  }
  
  // 3. Verificar acceso completo (Xerox)
  if (requireFullAccess) {
    if (!hasFullAccess()) {
      console.warn('ProtectedRoute: Usuario no tiene acceso completo (no es Xerox)');
      return <Navigate to={redirectTo} replace />;
    }
    return <>{children}</>;
  }
  
  // 4. Verificar permisos de grupo
  let hasGroupPermission = true; // Por defecto true si no se especifica grupo
  
  if (groupId !== undefined && groupId !== null) {
    // Verificar grupo único
    hasGroupPermission = isUserInGroup(groupId);
    
    if (!hasGroupPermission) {
      console.warn(`ProtectedRoute: Usuario no pertenece al grupo ${groupId}`);
      return <Navigate to={redirectTo} replace />;
    }
  } else if (anyGroup && Array.isArray(anyGroup)) {
    // Verificar si pertenece a al menos uno de los grupos
    hasGroupPermission = anyGroup.some(gId => isUserInGroup(gId));
    
    if (!hasGroupPermission) {
      console.warn(`ProtectedRoute: Usuario no pertenece a ninguno de los grupos: ${anyGroup.join(', ')}`);
      return <Navigate to={redirectTo} replace />;
    }
  }
  
  // 5. Verificar permisos de módulo (solo si se especificaron)
  let hasModulePermission = true; // Por defecto true si no se especifica módulo
  
  if (module) {
    // Verificar módulo único (Xerox siempre tiene acceso)
    hasModulePermission = canViewModule(module);
  } else if (anyOf) {
    // Verificar si tiene al menos uno de los módulos (Xerox siempre tiene acceso)
    hasModulePermission = hasFullAccess() || hasAnyModule(anyOf);
  } else if (allOf) {
    // Verificar si tiene todos los módulos (Xerox siempre tiene acceso)
    hasModulePermission = hasFullAccess() || hasAllModules(allOf);
  }
  
  // 6. Renderizar componente si tiene permiso, redirigir si no
  if (!hasModulePermission) {
    console.warn(`ProtectedRoute: Usuario sin permisos para módulo(s): ${module || anyOf || allOf}`);
    return <Navigate to={redirectTo} replace />;
  }
  
  return <>{children}</>;
};

export default ProtectedRoute;
